import { OverflowTabsMenu } from "@/components/detail-page/_components/tabbar/overflow-tabs-menu";
import { SectionTabTriggers } from "@/components/detail-page/_components/tabbar/section-tab-triggers";
import { useVisibleTabCount } from "@/components/detail-page/_hooks/use-visible-tab-count";
import type { DetailTab } from "@/components/detail-page/types";
import { TabsList } from "@/components/ui/tabs";
import { ChevronDownIcon } from "lucide-react";
import type { ReactElement, RefObject } from "react";
import { useTranslation } from "react-i18next";

interface CollapsingTabBarProps {
  sections: DetailTab[];
  activeTab: string | undefined;
  onSelectTab: (value: string) => void;
}

export function CollapsingTabBar({ sections, activeTab, onSelectTab }: CollapsingTabBarProps): ReactElement {
  const { rowRef, measureRef, visibleCount } = useVisibleTabCount(sections.map((section) => section.label));

  const visibleSections = sections.slice(0, visibleCount);
  const overflowSections = sections.slice(visibleCount);

  return (
    <>
      <InvisibleTabMeasureBar measureRef={measureRef} sections={sections} />
      <div ref={rowRef} className="border-border flex items-center border-b">
        <TabsList className="min-w-0 flex-1 border-b-0">
          <SectionTabTriggers sections={visibleSections} />
          {overflowSections.length > 0 && (
            <OverflowTabsMenu sections={overflowSections} activeTab={activeTab} onSelectTab={onSelectTab} />
          )}
        </TabsList>
      </div>
    </>
  );
}

interface InvisibleTabMeasureBarProps {
  measureRef: RefObject<HTMLDivElement | null>;
  sections: DetailTab[];
}

function InvisibleTabMeasureBar({ measureRef, sections }: InvisibleTabMeasureBarProps): ReactElement {
  const { t } = useTranslation("common");

  return (
    <div
      ref={measureRef}
      aria-hidden="true"
      className="pointer-events-none invisible absolute left-0 flex w-full gap-1 overflow-hidden text-sm"
    >
      {sections.map((section) => (
        <span key={section.id} className="inline-flex items-center gap-2 px-3 py-2 font-medium whitespace-nowrap">
          {section.icon && <section.icon className="size-4" />}
          {section.label}
        </span>
      ))}
      <span className="inline-flex items-center gap-1 px-3 py-2 font-medium whitespace-nowrap">
        {t("common:More")}
        <ChevronDownIcon className="size-4" />
      </span>
    </div>
  );
}
